/**
 * Resident Application Component
 *
 * This component handles the route `/resident-application/:applicantId`
 * It renders the appropriate application tab based on location state.
 * Defaults to 'about' tab if no state is provided.
 * Handles AWAITING, DENIED, EXPIRED status workflows per spec:
 * - AWAITING/VOID: show Application Update dialog with Submit for Processing
 * - applicant_lock === '1': show lock dialog (Under Review or Awaiting Co-Applicant)
 * - EXPIRED: show Expired dialog
 */

import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  fetchApplicationById,
  fetchApplications,
  selectApplicationData,
  selectApplicationLoading,
  selectApplicationError,
  selectHasSelectedApplication,
  setHasMultipleActiveApplications,
  setHasSelectedApplication,
} from "@/features/applicant/applicationSlice";
import { Loader2 } from "lucide-react";
import { ApplicationRouteGuard } from "@components/guards/ApplicationRouteGuard";
import { About } from "./About";
import { Property } from "./Property";
import { Income } from "./Income";
import { History } from "./History";
import { Signature } from "./Signature";
import { PaymentPage as Payment } from "./Payment";
import { AwaitingReasonDialog } from "@/features/applicant/components/AwaitingReasonDialog";
import { ApplicantLockDialog } from "@/features/applicant/components/ApplicantLockDialog";
import { ExpiredApplicationDialog } from "@/features/applicant/components/ExpiredApplicationDialog";
import { CoApplicantPaymentModal } from "@/features/applicant/components/CoApplicantPaymentModal";
import { ConfirmDialog } from "@components/ui/ConfirmDialog";
import { applicantConfirmDialogProps } from "@/features/applicant/constants/applicantDialogProps";
import { SubmitForProcessingProvider } from "@/features/applicant/context/SubmitForProcessingContext";
import {
  APPLICATION_TABS,
  getCurrentTabFromLocation,
  getTabPathWithApplicantId,
  type ApplicationTab,
} from "@lib/utils/applicationNavigation";
import { checkApplicationPaid } from "@/types/application";
import { showInfo } from "@lib/utils/notifications";
import { logoutAndRedirect } from "@/lib/api/authService";
import { logout } from "@/features/auth/authSlice";
import { majorSectionForTab, trackApplicationSectionView } from "@/lib/utils/analytics";

export function ResidentApplication() {
  const { applicantId } = useParams<{ applicantId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const applicationData = useAppSelector(selectApplicationData);
  const isLoading = useAppSelector(selectApplicationLoading);
  const error = useAppSelector(selectApplicationError);
  const hasSelectedApplication = useAppSelector(selectHasSelectedApplication);

  // Use refs to track the last fetched applicantId and prevent duplicate calls
  const lastFetchedIdRef = useRef<string | number | null>(null);
  const isFetchingRef = useRef<boolean>(false);
  // Only trigger status dialogs once per load for this applicantId
  const statusDialogsTriggeredRef = useRef<string | null>(null);

  // Track whether fetchApplicationById has completed for this applicant
  // This prevents the loader from disappearing before complete data is loaded
  const [fetchByIdCompleted, setFetchByIdCompleted] = useState(false);

  // Status workflow dialogs (AWAITING, lock, EXPIRED)
  const [showLockDialog, setShowLockDialog] = useState(false);
  const [showAwaitingDialog, setShowAwaitingDialog] = useState(false);
  const [showExpiredDialog, setShowExpiredDialog] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const paymentModalDismissedRef = useRef(false);
  const lastViewedSectionRef = useRef<string | null>(null);
  const [showCreditReportDialog, setShowCreditReportDialog] = useState(false);

  // Get current tab from location state (URL has no ?tab= query)
  const currentTab = getCurrentTabFromLocation(location);
  const isValidTab = APPLICATION_TABS.includes(currentTab);
  const activeTab: ApplicationTab = isValidTab ? currentTab : "about";

  // Track if we have the application data for this applicantId
  const hasApplicationData =
    applicationData?.applicant_id &&
    String(applicationData.applicant_id) === String(applicantId);

  // Show loader until fetchApplicationById has completed for this applicant.
  // This prevents the loader from disappearing after fetchApplications completes
  // (which only returns list/summary data) before the full application data is loaded.
  // During save operations, fetchByIdCompleted remains true so no loader is shown.
  const shouldShowLoader =
    (!hasApplicationData || !fetchByIdCompleted) && !error;

  // Match Angular's checkActiveApplications(): when user hasn't explicitly selected
  // an application, fetch the full list and only redirect to dashboard if THIS applicantId
  // is among the active apps AND there are multiple active apps. Non-active apps (EXPIRED,
  // CANCELLED, etc.) can always be viewed directly even when multiple active apps exist.
  const multipleActiveCheckedRef = useRef<boolean>(false);

  useEffect(() => {
    if (multipleActiveCheckedRef.current) return;

    if (hasSelectedApplication) {
      multipleActiveCheckedRef.current = true;
      return;
    }

    dispatch(
      fetchApplications({ sortBy: "lease_start_date", sortOrder: "desc" }),
    )
      .unwrap()
      .then((apps) => {
        const activeApps = apps.filter(
          (app) =>
            app.active === true ||
            (app.active !== undefined && String(app.active) === "1") ||
            (app.active !== undefined && Number(app.active) === 1),
        );

        if (activeApps.length > 1) {
          const isActiveApp = activeApps.some(
            (app) => String(app.applicant_id) === String(applicantId),
          );

          if (isActiveApp) {
            dispatch(setHasMultipleActiveApplications(true));
            dispatch(setHasSelectedApplication(false));
            multipleActiveCheckedRef.current = true;
            showInfo(
              "We have found multiple active invites and applications, please select one of them to proceed.",
              0,
            );
            navigate("/applicant/dashboard", { replace: true });
            return;
          }
        }

        if (activeApps.length <= 1) {
          dispatch(setHasMultipleActiveApplications(false));
        }
        dispatch(setHasSelectedApplication(true));
        multipleActiveCheckedRef.current = true;
      })
      .catch(() => {
        multipleActiveCheckedRef.current = true;
      });
  }, [
    applicantId,
    hasSelectedApplication,
    dispatch,
    navigate,
  ]);

  // Extract and validate applicantId from URL and fetch if needed
  // This effect only runs when applicantId changes, preventing duplicate calls
  useEffect(() => {
    // Don't proceed if we're still checking for multiple active applications
    if (!multipleActiveCheckedRef.current && !hasSelectedApplication) return;

    if (!applicantId) {
      // No applicantId in URL - redirect to application list
      navigate("/applicant/dashboard", { replace: true });
      return;
    }

    // Parse applicantId to ensure it's a valid number
    const parsedId = parseInt(applicantId, 10);
    if (isNaN(parsedId)) {
      // Invalid applicantId - redirect to application list
      navigate("/applicant/dashboard", { replace: true });
      return;
    }

    // Reset refs and fetchByIdCompleted when applicantId changes (allows fetching new ID)
    if (lastFetchedIdRef.current !== parsedId) {
      lastFetchedIdRef.current = null;
      isFetchingRef.current = false;
      setFetchByIdCompleted(false);
    }

    // Check if we've already fetched this ID or are currently fetching it
    const alreadyFetched = lastFetchedIdRef.current === parsedId;
    const currentlyFetching = isFetchingRef.current;

    // Always fetch by ID to ensure complete application data is loaded.
    // fetchApplications may have already populated applicationData with list/summary data,
    // but fetchApplicationById returns the full application data needed by tab components.
    // URL cleanup (removing ?tab= query) is handled by the separate useEffect below.
    if (!alreadyFetched && !currentlyFetching) {
      isFetchingRef.current = true;
      lastFetchedIdRef.current = parsedId;

      dispatch(fetchApplicationById(parsedId))
        .unwrap()
        .then(() => {
          isFetchingRef.current = false;
          setFetchByIdCompleted(true);
        })
        .catch((error) => {
          // Error loading application - redirect to application list
          console.error("Failed to load application:", error);
          isFetchingRef.current = false;
          lastFetchedIdRef.current = null;
          navigate("/applicant/dashboard", { replace: true });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    applicantId,
    dispatch,
    navigate,
    location.search,
    activeTab,
    hasSelectedApplication,
  ]); // Only depend on applicantId to prevent re-fetches

  // Strip ?tab= from URL when present (keep URL clean)
  useEffect(() => {
    if (
      applicationData?.applicant_id &&
      applicantId != null &&
      String(applicationData.applicant_id) === String(applicantId) &&
      location.search
    ) {
      navigate(getTabPathWithApplicantId(activeTab, applicantId), {
        state: { tab: activeTab },
        replace: true,
      });
    }
  }, [
    applicationData?.applicant_id,
    applicantId,
    navigate,
    activeTab,
    location.search,
  ]);

  // Handle error state
  useEffect(() => {
    if (error && !isLoading) {
      // Error occurred - redirect to application list
      navigate("/applicant/dashboard", { replace: true });
    }
  }, [error, isLoading, navigate]);

  // Reset status-dialogs trigger when applicantId changes so we can show dialogs for new application
  useEffect(() => {
    if (applicantId) {
      statusDialogsTriggeredRef.current = null;
      paymentModalDismissedRef.current = false;
    }
  }, [applicantId]);

  // AWAITING / DENIED / EXPIRED workflows: after load, if fromRoute (we're on resident-application/:id), show dialogs or refresh
  useEffect(() => {
    if (!applicantId || !hasApplicationData || isLoading) return;
    const key = String(applicantId);
    if (statusDialogsTriggeredRef.current === key) return;
    statusDialogsTriggeredRef.current = key;

    const status = (
      applicationData?.applicant_status ||
      applicationData?.application_status ||
      ""
    ).toUpperCase();
    const applicantLock = applicationData?.applicant_lock === "1";

    // Lock dialog only when applicant_lock === '1' (do not auto-show Application Update / Submit popup on open)
    if (applicantLock) setShowLockDialog(true);

    // AWAITING / VOID: show Application Update dialog with awaiting reasons (match Angular awaiting-reason popup)
    if (status === "AWAITING" || status === "VOID") {
      setShowAwaitingDialog(true);
    }

    // EXPIRED: show Expired dialog (matching Angular behavior – no redirect to dashboard)
    if (status === "EXPIRED") {
      setShowExpiredDialog(true);
    }
  }, [
    applicantId,
    hasApplicationData,
    isLoading,
    applicationData?.applicant_status,
    applicationData?.application_status,
    applicationData?.applicant_lock,
    applicationData?.progress_status_date,
    dispatch,
    navigate,
  ]);

  // Check for unpaid application fee and show modal (mirrors AngularJS checkApplicationPaid flow)
  useEffect(() => {
    // Only check if we have data and aren't loading
    if (!applicationData || isLoading) return;

    // Don't show if already on payment tab
    if (activeTab === 'payment') {
      setShowPaymentModal(false);
      return;
    }

    // Once user dismisses the modal, don't re-open it in the same session
    if (paymentModalDismissedRef.current) return;

    // Don't show if application is in a state where payment isn't allowed/relevant
    const status = (
      applicationData.applicant_status ||
      applicationData.application_status ||
      ''
    ).toUpperCase();

    if (['DENIED', 'CANCELLED', 'EXPIRED', 'VOID'].includes(status)) return;

    // Don't show if there's no application fee or fee is zero
    const feeAmount = Number(applicationData.application_fee || 0);
    if (feeAmount <= 0) return;

    // Don't show if no application_fee_invoice has been generated (fee not yet due)
    const applicationFeeInvoice = applicationData.application_fee_invoice;
    const hasInvoice = !!applicationFeeInvoice &&
      (applicationFeeInvoice === true || applicationFeeInvoice === '1' || applicationFeeInvoice === 1);
    if (!hasInvoice) return;

    // Check if application fee is paid
    const isPaid = checkApplicationPaid(applicationData);

    if (!isPaid) {
      setShowPaymentModal(true);
    }
  }, [applicationData, isLoading, activeTab]);

  useEffect(() => {
    lastViewedSectionRef.current = null;
  }, [applicantId]);

  useEffect(() => {
    if (shouldShowLoader || !hasApplicationData) return;
    const major = majorSectionForTab(activeTab);
    if (!major || lastViewedSectionRef.current === major.section) return;
    lastViewedSectionRef.current = major.section;
    trackApplicationSectionView(major.section, major.sectionNumber);
  }, [activeTab, applicantId, hasApplicationData, shouldShowLoader]);

  const handleCloseLockDialog = () => {
    setShowLockDialog(false);
  };

  const handlePaymentModalClose = () => {
    paymentModalDismissedRef.current = true;
    setShowPaymentModal(false);
  };

  const handlePaymentModalPay = () => {
    paymentModalDismissedRef.current = true;
    setShowPaymentModal(false);

    // Check if credit report is created
    if (applicationData?.credit_report !== '1') {
      setShowCreditReportDialog(true);
    } else {
      navigateToPayment();
    }
  };

  const navigateToPayment = () => {
    if (applicantId) {
      const path = getTabPathWithApplicantId('payment', applicantId);
      navigate(path, {
        state: { tab: 'payment' }
      });
    } else {
      console.error("Cannot navigate to payment: Missing applicantId");
    }
  };

  const handleCreditReportConfirm = () => {
    setShowCreditReportDialog(false);
    navigateToPayment();
  };

  // Show loading state only on initial load (when we don't have application data yet)
  // Don't show loader during save operations - the form components handle their own loading states
  if (shouldShowLoader) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="text-gray-600">Loading application...</span>
        </div>
      </div>
    );
  }

  // If we don't have application data and we're not loading, something went wrong
  // But don't show loader - let the component render and handle the error
  if (!hasApplicationData && !isLoading) {
    // Application data not available - this might be an error state
    // Let the component render anyway, it will handle the empty state
  }

  // Render the appropriate tab component
  const renderTab = () => {
    switch (activeTab) {
      case "about":
        return <About />;
      case "property":
        return <Property />;
      case "income":
        return <Income />;
      case "history":
        return <History />;
      case "signature":
        return <Signature />;
      case "payment":
        return <Payment />;
      default:
        return <About />;
    }
  };

  return (
    <SubmitForProcessingProvider
      applicationData={applicationData}
      onLogout={() => {
        dispatch(logout());
        logoutAndRedirect();
      }}
      onSuccessNavigate={() =>
        navigate("/applicant/dashboard", { replace: true })
      }
    >
      <ApplicationRouteGuard>
        {renderTab()}
        <ApplicantLockDialog
          open={showLockDialog}
          onClose={handleCloseLockDialog}
          applicationData={applicationData}
        />
        <AwaitingReasonDialog
          open={showAwaitingDialog}
          onClose={() => setShowAwaitingDialog(false)}
          applicationData={applicationData}
        />
        <ExpiredApplicationDialog
          open={showExpiredDialog}
          onClose={() => setShowExpiredDialog(false)}
          contactEmail={
            applicationData?.property?.email || applicationData?.agent?.email
          }
        />
        <CoApplicantPaymentModal
          open={showPaymentModal}
          onClose={handlePaymentModalClose}
          onPay={handlePaymentModalPay}
          applicationData={applicationData}
        />
        <ConfirmDialog
          {...applicantConfirmDialogProps}
          isOpen={showCreditReportDialog}
          title="Status Update"
          message="Credit Report not created, Do you want to continue?"
          primaryLabel="Yes"
          onPrimary={handleCreditReportConfirm}
          secondaryLabel="No"
          onSecondary={() => setShowCreditReportDialog(false)}
        />
      </ApplicationRouteGuard>
    </SubmitForProcessingProvider>
  );
}
